import { QueryStatus, UseQueryOptions } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { useDoClearSession } from '../../api/clear-session.api';
import { DiscoveryResponse, useDoDiscovery, Workspace } from '../../api/discovery.api';
import { Step } from '../../App';

type LoadDiscoveryResult = {
	status: QueryStatus;
	email?: string;
	/** Discovery workspace that matches the URL `workspaceId`. Undefined when the identity cookie belongs to another email. */
	workspace?: Workspace;
	onGoBack: () => void;
	/** Clears the identity cookie, then navigates to the email step. */
	clearSessionAndGoToEmail: () => void;
};

export const useLoadDiscovery = (queryOptions?: UseQueryOptions<DiscoveryResponse>): LoadDiscoveryResult => {
	const navigateTo = useNavigate();

	const { mutate: clearSession } = useDoClearSession({
		onSettled: () => navigateTo(Step.ENTER_EMAIL, { replace: true }),
	});

	const { workspaceId } = useParams<{ workspaceId: string }>();

	const queryData = useDoDiscovery<DiscoveryResponse>(
		{},
		{
			cacheTime: 0,
			retry: false,
			...queryOptions,
		}
	);

	const workspaces = queryData.data?.workspaces || [];
	const strategiesForWorkspace = workspaces.filter(({ id }) => id === workspaceId);

	const clearSessionAndGoToEmail = () => clearSession();

	const onGoBack = () => {
		// The identity cookie belongs to an email that has no access to this workspace.
		// Going to the workspace list would loop, so clear the session to let the user enter another email.
		if (strategiesForWorkspace.length === 0) {
			return clearSessionAndGoToEmail();
		}

		if (strategiesForWorkspace.length === 2) {
			return navigateTo(Step.SELECT_AUTHENTICATION_MULTI_STRATEGY_STEP);
		}

		if (workspaces.length > 1) {
			return navigateTo(Step.SELECT_WORKSPACE);
		}

		return clearSessionAndGoToEmail();
	};

	return {
		status: queryData.status,
		email: queryData.data?.email,
		workspace: strategiesForWorkspace[0],
		onGoBack,
		clearSessionAndGoToEmail,
	};
};
